/**
 * Costing visibility rules for Range Plan custom-section columns.
 *
 * A user without costing rights must not see costing values, so every column that reads them —
 * directly or through another custom column — is hidden from the column lists, the product table
 * and the column editors.
 */

export const COSTING_LOCATION = 'Costing_Information';

/** A location reference as stored on a column's `source` or on one formula step. */
interface LocationReference {
  location?: { name?: unknown } | null;
  subLocation?: { name?: unknown } | null;
}

/** The parts of a custom-section column that decide whether it reads costing data. */
export interface CustomSectionColumnLike {
  _id?: unknown;
  columnType?: unknown;
  source?: LocationReference | null;
  formulaArr?: Array<LocationReference | null> | null;
}

/**
 * Reads a column's type from either shape it arrives in: the saved string or the editor's option.
 * @param column the column
 * @returns the lower-case column type, or an empty string
 */
function readColumnType(column: CustomSectionColumnLike): string {
  const rawType = column.columnType;
  const typeName = rawType && typeof rawType === 'object' ? (rawType as { name?: unknown }).name : rawType;
  return typeof typeName === 'string' ? typeName.toLowerCase() : '';
}

/**
 * Lists the location references a column reads from.
 * @param column the column
 * @returns the source reference for a source column, the formula steps for a calculation column
 */
function readReferences(column: CustomSectionColumnLike): LocationReference[] {
  const columnType = readColumnType(column);
  if (columnType === 'source') {
    return column.source ? [column.source] : [];
  }
  if (columnType === 'calculation') {
    return (column.formulaArr ?? []).filter((step): step is LocationReference => Boolean(step));
  }
  return [];
}

/**
 * Indexes columns by id so references to other custom columns can be followed.
 * @param columns every column that may be referenced
 * @returns a map from column id to column
 */
export function indexColumnsById<T extends CustomSectionColumnLike>(columns: T[] | null | undefined): Map<string, T> {
  const byId = new Map<string, T>();
  (columns ?? []).forEach((column) => {
    if (column?._id !== undefined && column?._id !== null) {
      byId.set(String(column._id), column);
    }
  });
  return byId;
}

/**
 * True when a column shows costing data: a source column located in Costing Information, a
 * calculation with a Costing Information step, or a column that sources or calculates from another
 * custom column which itself shows costing data. References to columns missing from
 * `columnsById` cannot be followed and count as non-costing.
 * @param column the column to test
 * @param columnsById every loaded column, for following references to other custom columns
 * @param visited ids already on the current reference chain, guarding against cycles
 * @returns whether the column must be hidden from users without costing rights
 */
export function isCostingDerivedColumn(
  column: CustomSectionColumnLike | null | undefined,
  columnsById: Map<string, CustomSectionColumnLike> = new Map(),
  visited: Set<string> = new Set(),
): boolean {
  if (!column) {
    return false;
  }
  const ownId = column._id !== undefined && column._id !== null ? String(column._id) : null;
  if (ownId) {
    if (visited.has(ownId)) {
      return false;
    }
    visited.add(ownId);
  }
  return readReferences(column).some((reference) => {
    if (reference.location?.name === COSTING_LOCATION) {
      return true;
    }
    const referencedId = reference.subLocation?.name;
    const referencedColumn = referencedId !== undefined && referencedId !== null ? columnsById.get(String(referencedId)) : undefined;
    return referencedColumn ? isCostingDerivedColumn(referencedColumn, columnsById, visited) : false;
  });
}

/**
 * Removes costing-derived columns when the user has no costing access.
 *
 * Returns the same array instance whenever nothing is removed, so effects keyed on the column list
 * do not re-run for users who see every column.
 * @param columns the columns to show
 * @param hasCostingAccess whether the user may see costing data
 * @param referenceColumns every loaded column, for following references; defaults to `columns`
 * @returns the visible columns
 */
export function hideCostingColumns<T extends CustomSectionColumnLike>(
  columns: T[] | null | undefined,
  hasCostingAccess: boolean,
  referenceColumns?: CustomSectionColumnLike[] | null,
): T[] | null | undefined {
  if (hasCostingAccess || !Array.isArray(columns) || columns.length === 0) {
    return columns;
  }
  const columnsById = indexColumnsById<CustomSectionColumnLike>([...(referenceColumns ?? []), ...columns]);
  const visibleColumns = columns.filter((column) => !isCostingDerivedColumn(column, columnsById));
  return visibleColumns.length === columns.length ? columns : visibleColumns;
}

/**
 * Applies a reorder of the visible columns to the full column list.
 *
 * Columns the user cannot see keep the slots they occupied; the visible columns fill the remaining
 * slots in their new order. Saving positions for the visible list alone would leave the hidden
 * columns on stale positions and scramble the order other users see.
 * @param allColumns every column of the section, in saved order
 * @param reorderedVisibleColumns the visible columns in their new order
 * @returns every column in the order to save
 */
export function mergeVisibleOrder<T extends { _id?: unknown }>(allColumns: T[], reorderedVisibleColumns: T[]): T[] {
  const visibleIds = new Set(reorderedVisibleColumns.map((column) => String(column._id)));
  const queue = [...reorderedVisibleColumns];
  return allColumns.map((column) => (visibleIds.has(String(column._id)) ? queue.shift() ?? column : column));
}
